import {
  ORG_ROLES,
  ORG_ROLE_PERMISSIONS,
  PLATFORM_PERMISSIONS,
  orgRolesAdminister,
  type AccessContext,
  type EmployeeContext,
  type Member,
  type OrgRole,
  type PlatformPermission,
} from '@traceio-org/platform-contracts';

/**
 * What the organization settings area offers a person, from the organization permissions platform-api reports for
 * them (me/access `administration` for the entry points, the overview's `viewer` inside an organization). It only
 * shapes the screens (a billing administrator sees no member list, an administrator no owner role to hand out):
 * platform-api reads the membership again on every request and stays the authority, so a role changed a moment ago
 * is refused there even while this page still offers it.
 */
export interface SettingsAbilities {
  /** The overview: shown to whoever administers anything beyond billing. */
  overview: boolean;
  /** Members and invitations (members.read). */
  readMembers: boolean;
  /** Invite, change roles and products, suspend, reactivate and remove (members.manage). */
  manageMembers: boolean;
  /** Modules: their state, turning them on and off, configuration, the organization's emergency stop. */
  configureModules: boolean;
  /** Custom hosts and email domains (domains.read). */
  readDomains: boolean;
  /** Add, verify, disable, enable and release them (domains.manage). */
  manageDomains: boolean;
  /** The organization's audit log (audit.read). */
  readAudit: boolean;
  /** The billing section: contracts, prices, usage and invoices (billing.read: owners, administrators, billing administrators). */
  readBilling: boolean;
  /** The spend cap change (billing.manage: owners and billing administrators). */
  manageBilling: boolean;
  /** The Help desk section: every ticket of the organization (tickets.read_all: help_desk, org_admin, org_owner). */
  readTickets: boolean;
  /** Assign, resolve, add customer notes, file on behalf of a teammate, send straight to Trace (tickets.manage). */
  manageTickets: boolean;
  /** Escalate a ticket to Trace (tickets.escalate). */
  escalateTickets: boolean;
  /** Change where new tickets go (org.update: owners and administrators, never the help desk role). */
  changeTicketRouting: boolean;
}

export const SETTINGS_SECTIONS = [
  'overview',
  'members',
  'invitations',
  'modules',
  'domains',
  'help-desk',
  'audit',
  'billing',
] as const;
export type SettingsSection = (typeof SETTINGS_SECTIONS)[number];

/** Only permissions the contract knows count; anything else grants nothing. */
function knownPermissions(permissions: Iterable<string>): Set<PlatformPermission> {
  const known = new Set<PlatformPermission>();
  for (const permission of permissions) {
    if ((PLATFORM_PERMISSIONS as readonly string[]).includes(permission))
      known.add(permission as PlatformPermission);
  }
  return known;
}

/** The permissions of a role set (ORG_ROLE_PERMISSIONS); a role the contract does not know grants nothing. */
export function permissionsOfRoles(roles: readonly string[]): Set<PlatformPermission> {
  return new Set(
    roles.flatMap((role) =>
      (ORG_ROLES as readonly string[]).includes(role) ? ORG_ROLE_PERMISSIONS[role as OrgRole] : [],
    ),
  );
}

export function settingsAbilities(permissions: Iterable<string>): SettingsAbilities {
  const held = knownPermissions(permissions);
  const has = (permission: PlatformPermission) => held.has(permission);
  const readMembers = has('members.read');
  const configureModules = has('modules.configure');
  const readDomains = has('domains.read');
  const readAudit = has('audit.read');
  const readBilling = has('billing.read');
  const readTickets = has('tickets.read_all');
  return {
    overview: readMembers || configureModules || readDomains || readAudit,
    readMembers,
    manageMembers: readMembers && has('members.manage'),
    configureModules,
    readDomains,
    manageDomains: readDomains && has('domains.manage'),
    readAudit,
    readBilling,
    manageBilling: readBilling && has('billing.manage'),
    readTickets,
    manageTickets: readTickets && has('tickets.manage'),
    escalateTickets: readTickets && has('tickets.escalate'),
    changeTicketRouting: readTickets && has('org.update'),
  };
}

const SECTION_ABILITY: Readonly<Record<SettingsSection, keyof SettingsAbilities>> = {
  overview: 'overview',
  members: 'readMembers',
  invitations: 'readMembers',
  modules: 'configureModules',
  domains: 'readDomains',
  'help-desk': 'readTickets',
  audit: 'readAudit',
  billing: 'readBilling',
};

/** The sections a person sees, in navigation order. */
export function visibleSections(abilities: SettingsAbilities): SettingsSection[] {
  return SETTINGS_SECTIONS.filter((section) => abilities[SECTION_ABILITY[section]]);
}

/**
 * The ticket permissions of a role set (tickets.*, with org.read and products.read every member holds). The only
 * permissions the settings take from roles alone: tickets.* never make an administrator, so me/access carries no
 * administration for a help desk member (orgRolesAdminister), and the help desk is decided from the roles.
 */
export function ticketPermissionsOfRoles(roles: readonly string[]): PlatformPermission[] {
  return [...permissionsOfRoles(roles)].filter(
    (permission) =>
      permission === 'org.read' || permission === 'products.read' || permission.startsWith('tickets.'),
  );
}

/**
 * A workspace the person reaches only as its help desk: no administration (platform-api answers every organization
 * administration route 403, the overview included), roles that read all tickets. The settings show it the Help desk
 * section alone, without reading the overview.
 */
export function isHelpDeskOnly(context: EmployeeContext): boolean {
  const roles = context.roles.filter((role): role is OrgRole =>
    (ORG_ROLES as readonly string[]).includes(role),
  );
  return (
    context.administration === undefined &&
    // An administrator's roles without administration mean a suspended membership or organization (me/access then
    // leaves the field out): nothing is offered, as before. Only roles that never administer (help_desk) qualify.
    !orgRolesAdminister(roles) &&
    // A suspended membership or organization shows on every product; nothing of it is offered either.
    !context.products.some(
      (product) => product.reason === 'membership_suspended' || product.reason === 'organization_suspended',
    ) &&
    settingsAbilities(ticketPermissionsOfRoles(roles)).readTickets
  );
}

/**
 * The permissions the settings entry points read for a workspace: me/access's administration where it is there,
 * else the ticket permissions of the roles (a help desk member). Inside an organization the overview's viewer
 * replaces them for administrators.
 */
export function contextPermissions(context: EmployeeContext): readonly PlatformPermission[] {
  if (context.administration) return context.administration.permissions;
  return isHelpDeskOnly(context) ? ticketPermissionsOfRoles(context.roles) : [];
}

/**
 * The employee workspaces whose settings a person may open: those me/access marks as administered (an active
 * membership of an active organization whose roles hold an administrative permission; platform-api leaves the
 * field out for everyone else) where the permissions show at least one section, and those where the roles work the
 * help desk (isHelpDeskOnly: SECTION_ABILITY then shows Help desk alone). Portal and staff contexts never qualify: a
 * portal's customers are not members of the organization, and Trace staff use Trace Admin.
 */
export function administrableContexts(contexts: readonly AccessContext[]): EmployeeContext[] {
  return contexts.filter(
    (context): context is EmployeeContext =>
      context.type === 'employee' &&
      visibleSections(settingsAbilities(contextPermissions(context))).length > 0,
  );
}

/**
 * The roles a person may hand out: those whose every permission they hold themselves. Only an owner holds every
 * permission, so only an owner grants the owner role, and an administrator (no billing changes) never grants owner
 * or billing administrator.
 */
export function grantableRoles(permissions: Iterable<string>): OrgRole[] {
  const held = knownPermissions(permissions);
  if (!held.has('members.manage')) return [];
  return ORG_ROLES.filter((role) => ORG_ROLE_PERMISSIONS[role].every((permission) => held.has(permission)));
}

export type MemberChangeVerdict =
  | { allowed: true }
  /** self: nobody changes their own membership; removed: history only; roles: they hold more than you. */
  | { allowed: false; why: 'self' | 'removed' | 'roles' | 'permission' };

/**
 * Whether the settings offer changes to a membership. The last active owner rule (never removed, suspended or
 * demoted) needs every owner of the organization, so platform-api alone decides it and the dialog shows its answer.
 */
export function canChangeMember(
  permissions: Iterable<string>,
  myMembershipId: string | null,
  member: Member,
): MemberChangeVerdict {
  const held = knownPermissions(permissions);
  if (!held.has('members.manage')) return { allowed: false, why: 'permission' };
  if (member.membershipId === myMembershipId) return { allowed: false, why: 'self' };
  if (member.status === 'removed') return { allowed: false, why: 'removed' };
  const theirs = permissionsOfRoles(member.roles);
  if (![...theirs].every((permission) => held.has(permission))) return { allowed: false, why: 'roles' };
  return { allowed: true };
}
